import { reactive, ref, watch } from 'vue';
import { listRecordPreviousRevisions } from '../api/records.api';
import { toRecordRevisionRows } from './records.types';
import type { RecordRevisionListRow } from './records.types';

export type RecordRevisionsStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface RecordRevisionsState {
  status: RecordRevisionsStatus;
  revisions: RecordRevisionListRow[];
}

const IDLE_STATE: RecordRevisionsState = { status: 'idle', revisions: [] };

/**
 * Expansion state of the record rows, holding the ids of every open row and the
 * previous revisions fetched for each of them.
 *
 * The revisions are loaded ON DEMAND: the listing never carries them, so the
 * first page costs one request and a row costs one more the first time it opens.
 * A row that is collapsed and reopened does not fetch again, because the state is
 * already `loaded`; a row that failed refetches, because `error` is retryable.
 *
 * More than one row stays open at the same time, which is the behaviour of the
 * prototype and differs from the document list, where a single row expands. The
 * expansion collapses whenever `resetOn` changes, because the ids of one query
 * say nothing about the next one: a page, quantity, term, ordering or filter
 * change replaces the visible set and an id kept from before would open a row
 * that has nothing to do with the one the user opened. The cache goes with it,
 * for the same reason.
 */
export function useRecordExpansion(resetOn: () => unknown) {
  const expandedIds = ref<Set<number>>(new Set());
  const revisionsById = reactive(new Map<number, RecordRevisionsState>());

  const isExpanded = (id: number) => expandedIds.value.has(id);

  const getRevisionsState = (id: number): RecordRevisionsState =>
    revisionsById.get(id) ?? IDLE_STATE;

  const load = async (id: number) => {
    revisionsById.set(id, { status: 'loading', revisions: [] });

    try {
      const revisions = await listRecordPreviousRevisions(id);
      revisionsById.set(id, { status: 'loaded', revisions: toRecordRevisionRows(revisions) });
    } catch (error) {
      console.error('Erro ao carregar revisões anteriores:', error);
      revisionsById.set(id, { status: 'error', revisions: [] });
    }
  };

  const toggle = (id: number) => {
    const next = new Set(expandedIds.value);

    if (next.has(id)) {
      next.delete(id);
      expandedIds.value = next;
      return;
    }

    next.add(id);
    expandedIds.value = next;

    const status = getRevisionsState(id).status;
    if (status === 'idle' || status === 'error') {
      void load(id);
    }
  };

  const retry = (id: number) => {
    void load(id);
  };

  /**
   * Rewrites the cached revisions of one row in place, without fetching again.
   *
   * A write that only changes the status of a sibling revision does not change WHICH revisions are
   * previous ones: that set is the revisions with a lower ordinal, and no write moves a row's own
   * ordinal. So the row that is already open keeps the list it has and only the changed cells are
   * patched. Refetching here made an expanded row reload its whole list on every toggle.
   */
  const patchRevisions = (
    id: number,
    patch: (revision: RecordRevisionListRow) => RecordRevisionListRow
  ) => {
    const state = revisionsById.get(id);

    if (!state || state.status !== 'loaded') return;

    revisionsById.set(id, { status: 'loaded', revisions: state.revisions.map(patch) });
  };

  /**
   * Forgets a row that left the listing: there is nothing to reload, and an id kept here would
   * open a row that is no longer there.
   */
  const remove = (id: number) => {
    revisionsById.delete(id);

    if (!expandedIds.value.has(id)) return;

    const next = new Set(expandedIds.value);
    next.delete(id);
    expandedIds.value = next;
  };

  const collapseAll = () => {
    expandedIds.value = new Set();
    revisionsById.clear();
  };

  watch(resetOn, collapseAll);

  return {
    expandedIds,
    isExpanded,
    getRevisionsState,
    toggle,
    retry,
    patchRevisions,
    remove,
    collapseAll,
  };
}
